import {
  leftReceptacle,
  leftSpout,
  toiletReceptacle,
  topSpout,
  type LevelConfig,
} from './portHelpers';

export const LEVEL_4: LevelConfig = {
  step: 4,
  title: 'STEP FOUR',
  instructions:
    'Specimen consumes a thin organic gruel. Connect esophagus → stomach → intestines, return blood, and route waste to the toilet.',
  ports: [
    // v5.0: esophagus now emits gruel only — stomach digests it into food.
    topSpout('s4-esophagus', 7, ['gruel']),
    leftReceptacle('s4-blood-return', 5, ['food', 'oxygen', 'co2']),
    leftSpout('s4-o2-blood', 8, ['oxygen']),
    // v5.1: toilet now expects 'waste' (intestines bottom-out emits waste, not gruel).
    toiletReceptacle('s4-toilet', 17),
  ],
  organPool: [
    {
      id: 'stomach',
      art: 'stomach',
      cols: 3,
      rows: 3,
      activatesWhen: ['s4-stomach-out'],
      ports: [
        // Top input: cone protrudes UP from the top of the stomach
        {
          id: 's4-stomach-in',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['gruel'],
        },
        // Bottom output: castellated piece anchored INSIDE the stomach's last row,
        // so the piece visually attaches to the organ (no floating gap).
        // v5.0 rule: emits [food] only when the input is receiving [gruel].
        {
          id: 's4-stomach-out',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 1, dRow: 2 },
          carries: ['food'],
          shape: 'castellated',
          dependsOn: ['s4-stomach-in'],
        },
      ],
    },
    {
      id: 'intestines',
      art: 'intestines',
      cols: 4,
      rows: 3,
      activatesWhen: ['s4-int-left-out', 's4-int-bottom'],
      ports: [
        // Top input (castellated, direct-link to stomach below).
        // v5.1: acceptsAny — students can feed anything in here without bursting;
        // the side-out's transform function reads the actual incoming mix and
        // decides what to do.
        {
          id: 's4-int-top',
          kind: 'receptacle',
          facing: 'N',
          cellOffset: { dCol: 1, dRow: -1 },
          carries: ['food'],          // cosmetic — porthole icon
          acceptsAny: true,
          shape: 'castellated',
        },
        // Left side input (blood with oxygen).
        // v5.1: also acceptsAny — passthrough takes whatever comes in.
        {
          id: 's4-int-left-in',
          kind: 'receptacle',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 0 },
          carries: ['oxygen'],        // cosmetic
          acceptsAny: true,
        },
        // Left side output: compositional rule.
        //   Passthrough whatever arrives at left-in, PLUS food iff top has food.
        //   This is the canonical "intestines absorbs food into the bloodstream"
        //   visual — student watches plain O₂ flow first, then the pipe enriches
        //   itself with food + CO₂ once food finishes its journey down through
        //   the stomach chain.
        {
          id: 's4-int-left-out',
          kind: 'spout',
          facing: 'W',
          cellOffset: { dCol: -1, dRow: 1 },
          carries: ['food', 'oxygen', 'co2'],  // cosmetic / fallback / best-case
          transform: (inputs) => {
            const top = inputs.get('s4-int-top') ?? [];
            const sideIn = inputs.get('s4-int-left-in') ?? [];
            const topHasFood = top.includes('food');
            if (sideIn.length === 0 && !topHasFood) return null;
            const out = new Set(sideIn);
            if (topHasFood) {
              out.add('food');
              // Intestines also add CO₂ (metabolic byproduct of digestion).
              out.add('co2');
            }
            return Array.from(out);
          },
        },
        // Bottom spout: waste to toilet. Fires when food arrives at the top.
        {
          id: 's4-int-bottom',
          kind: 'spout',
          facing: 'S',
          cellOffset: { dCol: 2, dRow: 3 },
          carries: ['waste'],
          transform: (inputs) => {
            const top = inputs.get('s4-int-top') ?? [];
            return top.includes('food') ? ['waste'] : null;
          },
        },
      ],
    },
  ],
};
